import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  DecideLunchDetentionData,
  LunchDetentionFilters,
  LunchDetentionNoteVisibility,
  MarkLunchDetentionDayData,
  ProposeLunchDetentionData,
  UpdateLunchDetentionData,
  lunchDetentionsApi,
  studentDirectoryKeys,
} from '@/services';

export const lunchDetentionsKeys = {
  all: ['lunch-detentions'] as const,
  list: (filters: LunchDetentionFilters) => ['lunch-detentions', 'list', filters] as const,
  detail: (id: string) => ['lunch-detentions', 'detail', id] as const,
  rooms: ['lunch-detentions', 'room'] as const,
  room: (date: string) => ['lunch-detentions', 'room', date] as const,
  unmarked: ['lunch-detentions', 'unmarked'] as const,
  // A prefix-child of `all` — invalidating `all` already invalidates this, so callers below only
  // ever need to invalidate `all` and never both.
  badges: ['lunch-detentions', 'badges'] as const,
  clashCheck: (studentIds: string[], startDate: string, endDate: string) =>
    ['lunch-detentions', 'clash-check', [...studentIds].sort(), startDate, endDate] as const,
};

export function useLunchDetentionsQuery(filters: LunchDetentionFilters = {}) {
  return useQuery({
    queryKey: lunchDetentionsKeys.list(filters),
    queryFn: () => lunchDetentionsApi.list(filters),
  });
}

export function useLunchDetentionQuery(id: string | undefined) {
  return useQuery({
    queryKey: lunchDetentionsKeys.detail(id ?? ''),
    queryFn: () => lunchDetentionsApi.get(id!),
    enabled: !!id,
  });
}

export function useTodayLunchDetentionsQuery(date?: string) {
  return useQuery({
    queryKey: lunchDetentionsKeys.room(date ?? 'today'),
    queryFn: () => lunchDetentionsApi.today(date),
  });
}

export function useUnmarkedLunchDetentionDaysQuery(enabled = true) {
  return useQuery({ queryKey: lunchDetentionsKeys.unmarked, queryFn: () => lunchDetentionsApi.unmarked(), enabled });
}

export function useLunchDetentionBadgesQuery(enabled = true) {
  return useQuery({ queryKey: lunchDetentionsKeys.badges, queryFn: () => lunchDetentionsApi.badges(), enabled });
}

/**
 * The propose dialog's pre-submit preview: as the teacher picks students and dates, this reports
 * the same clash/count a submit would produce, without creating anything. Disabled until there is
 * at least one student and both dates — the caller is expected to debounce fast-changing inputs
 * (see `useDebouncedValue`) before passing them in, so a click-through of several students settles
 * into one request rather than one per click.
 */
export function useLunchDetentionClashCheckQuery(studentIds: string[], startDate: string, endDate: string) {
  return useQuery({
    queryKey: lunchDetentionsKeys.clashCheck(studentIds, startDate, endDate),
    queryFn: () => lunchDetentionsApi.clashCheck(studentIds, startDate, endDate),
    enabled: studentIds.length > 0 && !!startDate && !!endDate,
  });
}

export function useLunchDetentionNotesQuery(id: string | undefined) {
  return useQuery({
    queryKey: [...lunchDetentionsKeys.detail(id ?? ''), 'notes'],
    queryFn: () => lunchDetentionsApi.notes(id!),
    enabled: !!id,
  });
}

export function usePostLunchDetentionNoteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body, visibility }: { id: string; body: string; visibility?: LunchDetentionNoteVisibility }) =>
      lunchDetentionsApi.postNote(id, body, visibility),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: lunchDetentionsKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: lunchDetentionsKeys.badges });
    },
  });
}

export function useMarkLunchDetentionNotesReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, lane }: { id: string; lane?: LunchDetentionNoteVisibility }) =>
      lunchDetentionsApi.markNotesRead(id, lane),
    onSuccess: () => void qc.invalidateQueries({ queryKey: lunchDetentionsKeys.badges }),
  });
}

export function useProposeLunchDetentionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ProposeLunchDetentionData) => lunchDetentionsApi.propose(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lunchDetentionsKeys.all });
    },
  });
}

export function useDecideLunchDetentionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DecideLunchDetentionData }) => lunchDetentionsApi.decide(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lunchDetentionsKeys.all });
      // An approval (or rejection) can start or clear the running-detention badge on the
      // student list and profile — those must not be left showing a stale mark.
      qc.invalidateQueries({ queryKey: studentDirectoryKeys.all });
    },
  });
}

export function useUpdateLunchDetentionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLunchDetentionData }) => lunchDetentionsApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lunchDetentionsKeys.all });
    },
  });
}

export function useCancelLunchDetentionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => lunchDetentionsApi.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lunchDetentionsKeys.all });
      // A cancellation ends the running detention — the student badge must clear immediately.
      qc.invalidateQueries({ queryKey: studentDirectoryKeys.all });
    },
  });
}

export function useWithdrawLunchDetentionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => lunchDetentionsApi.withdraw(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lunchDetentionsKeys.all });
    },
  });
}

export function useMarkLunchDetentionSeenMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => lunchDetentionsApi.markSeen(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lunchDetentionsKeys.all });
    },
  });
}

export function useMarkLunchDetentionDayMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dayId, data }: { dayId: string; data: MarkLunchDetentionDayData }) =>
      lunchDetentionsApi.markDay(dayId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lunchDetentionsKeys.all });
      qc.invalidateQueries({ queryKey: lunchDetentionsKeys.rooms });
      qc.invalidateQueries({ queryKey: lunchDetentionsKeys.unmarked });
      qc.invalidateQueries({ queryKey: lunchDetentionsKeys.badges });
    },
  });
}

export function useLunchDetentionDocumentsQuery(id: string | undefined) {
  return useQuery({
    queryKey: [...lunchDetentionsKeys.detail(id ?? ''), 'documents'],
    queryFn: () => lunchDetentionsApi.documents(id!),
    enabled: !!id,
  });
}

/** The count on the list and the detail page comes from the run itself, so both are refreshed. */
export function useUploadLunchDetentionDocumentsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, files }: { id: string; files: File[] }) => lunchDetentionsApi.uploadDocuments(id, files),
    onSuccess: () => void qc.invalidateQueries({ queryKey: lunchDetentionsKeys.all }),
  });
}

export function useDeleteLunchDetentionDocumentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => lunchDetentionsApi.deleteDocument(attachmentId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: lunchDetentionsKeys.all }),
  });
}
